import { PendingTemplatesComponent } from "@/components/pending";
import { QueryBoundary } from "@/components/query-boundary";
import { TemplateCreateWizard } from "@/components/template/template-create-wizard";
import { TemplateFilters } from "@/components/template/template-filters";
import TemplateList from "@/components/template/template-list";
import { TEMPLATE_TYPES, zodSlug } from "@papier/shared";
import { Button } from "@papier/ui/components/ui/button";
import { createFileRoute } from "@tanstack/react-router";
import { LayoutDashboard, Plus, Image } from "lucide-react";
import { useState } from "react";
import { z } from "zod";

const templateSearchSchema = z.object({
  view: z.enum(["grid", "list"]).catch("grid"),
  type: z.enum([...TEMPLATE_TYPES, "all"]).catch("all"),
  query: z
    .string()
    .optional()
    .transform((val) => (val?.length === 0 ? undefined : val)),
  env: zodSlug().catch("all"),
});

export const Route = createFileRoute("/_app/projects/$projectSlug/_layout/")({
  component: IndexProjectTemplates,
  validateSearch: templateSearchSchema,
});

function IndexProjectTemplates() {
  const { useNavigate, useSearch, useParams } = Route;
  const navigate = useNavigate();
  const { query, type, env } = useSearch({
    select: ({ query, type, env }) => ({ query, type, env }),
  });
  const { projectSlug } = useParams();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const openCreateWizard = () => {
    setIsDialogOpen(true);
  };

  const handleResetFilters = () => {
    navigate({
      search: (prev) => ({
        view: prev.view,
      }),
      replace: true,
    });
  };

  const hasActiveFilters = !!query || type !== "all" || env !== "all";

  return (
    <main>
      <section className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-foreground flex items-center gap-2 text-2xl font-bold">
            <LayoutDashboard className="size-5" />
            Templates
          </h1>
          <p className="text-foreground/80">Manage your organization templates</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => navigate({ to: "/projects/$projectSlug/media" })}
            variant="outline"
            className="text-foreground hover:text-foreground border-white/20"
          >
            <Image className="mr-2 size-4" />
            Media Gallery
          </Button>
          <Button
            onClick={openCreateWizard}
            className="glass-button text-foreground hover:text-foreground border-white/20"
          >
            <Plus />
            Create Template
          </Button>
        </div>
      </section>

      <section className="grid gap-4">
        <TemplateFilters hasActiveFilters={hasActiveFilters} onReset={handleResetFilters} />
        <QueryBoundary suspenseFallback={<PendingTemplatesComponent />}>
          <TemplateList hasActiveFilters={hasActiveFilters} openCreateWizard={openCreateWizard} />
        </QueryBoundary>
      </section>

      <TemplateCreateWizard
        open={isDialogOpen}
        onOpen={setIsDialogOpen}
        projectSlug={projectSlug}
      />
    </main>
  );
}
